import {inject, Injectable} from '@angular/core';
import {BUTTON_COLOR, BUTTON_SIZE, BUTTON_TYPE, IFvConfirmButton} from '@discocil/fv-components';
import {Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {GlobalConfirmService} from '@shared/services/global-confirm/global-confirm.service';

export interface PaymentChoiceOption<TValue> {
  labelKey: string;
  value: TValue;
  style?: {
    color?: BUTTON_COLOR;
    size?: BUTTON_SIZE;
    type?: BUTTON_TYPE;
  };
}

export interface PaymentInteractionConfig<TValue> {
  titleKey: string;
  descriptionKey?: string;
  descriptionParams?: Record<string, unknown>;
  options: PaymentChoiceOption<TValue>[];
  defaultValue: TValue;
}

@Injectable({providedIn: 'root'})
export class PaymentInteractionHandler {
  private readonly confirm = inject(GlobalConfirmService);
  private readonly i18n = inject(TranslateService);

  askUserChoice<TValue>(config: PaymentInteractionConfig<TValue>): Observable<TValue> {
    return new Observable<TValue>((observer) => {
      let result = config.defaultValue;

      this.confirm.open({
        noClose: false,
        hideCloseIcon: true,
        message: this.translate(config.titleKey),
        submessage: config.descriptionKey ? this.translate(config.descriptionKey, config.descriptionParams) : undefined,
        buttons: this.mapToConfirmButtons(config.options, (value) => {
          result = value;
          this.confirm.close();
        }),
        onClosed: () => {
          observer.next(result);
          observer.complete();
        },
      });

      return () => this.confirm.close();
    });
  }

  private mapToConfirmButtons<TValue>(options: PaymentChoiceOption<TValue>[], onSelect: (v: TValue) => void): IFvConfirmButton[] {
    return options.map(opt => ({
      text: this.translate(opt.labelKey),
      color: opt.style?.color ?? BUTTON_COLOR.primary,
      size: opt.style?.size ?? BUTTON_SIZE.full,
      type: opt.style?.type ?? BUTTON_TYPE.filled,
      handler: () => onSelect(opt.value),
    }));
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.i18n.instant(key, params);
  }
}
